export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

export const compressBase64Image = async (base64String: string, options: CompressionOptions = {}): Promise<string> => {
  const { maxWidth = 1200, maxHeight = 1200, quality = 0.85 } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;

          if (width > height) {
            width = maxWidth;
            height = Math.round(width / aspectRatio);
          } else {
            height = maxHeight;
            width = Math.round(height * aspectRatio);
          }
        }

        // Create canvas and compress
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with quality compression
        const compressedDataUrl = canvas.toDataURL("image/jpeg", quality);

        // Remove data URI prefix and return
        const base64 = compressedDataUrl.split(",")[1];
        resolve(base64);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for compression"));
    };

    // Set source (add data URI prefix if not present)
    const dataUrl = base64String.startsWith("data:") ? base64String : `data:image/png;base64,${base64String}`;

    img.src = dataUrl;
  });
};

export const compressMultipleImages = async (base64Strings: string[], options: CompressionOptions = {}): Promise<string[]> => {
  const compressed: string[] = [];

  for (const base64 of base64Strings) {
    try {
      const result = await compressBase64Image(base64, options);
      compressed.push(result);
    } catch (error) {
      console.error("Image compression failed:", error);
      // Fall back to original if compression fails
      compressed.push(base64);
    }
  }

  return compressed;
};

export const getBase64Size = (base64String: string): number => {
  // Remove data URI prefix if present
  const base64 = base64String.includes(",") ? base64String.split(",")[1] : base64String;

  // Base64 encoding increases size by ~33%
  // Actual size = (base64 length * 0.75) / (1024 * 1024)
  return (base64.length * 0.75) / (1024 * 1024);
};
